import { useTenantStore } from '@/stores/tenantStore'
import { useQuery } from '@tanstack/react-query'
import { fetchClient } from '../typedClient'

interface EntityObject {
  id: string
  goid: string
  name: string
  [key: string]: unknown
}

// Endpoint mapping for basic entity endpoints
type BasicEndpoint =
  | '/crossroads/network/companies/basic'
  | '/crossroads/entity/sites/basic'
  | '/crossroads/entity/tanks/basic'
  | '/crossroads/entity/products/basic'
  | '/crossroads/entity/terminals/basic'

const entityEndpointMap: Record<string, BasicEndpoint> = {
  counterparty: '/crossroads/network/companies/basic',
  counterparties: '/crossroads/network/companies/basic',
  company: '/crossroads/network/companies/basic',
  companies: '/crossroads/network/companies/basic',
  site: '/crossroads/entity/sites/basic',
  sites: '/crossroads/entity/sites/basic',
  tank: '/crossroads/entity/tanks/basic',
  tanks: '/crossroads/entity/tanks/basic',
  product: '/crossroads/entity/products/basic',
  products: '/crossroads/entity/products/basic',
  terminal: '/crossroads/entity/terminals/basic',
  terminals: '/crossroads/entity/terminals/basic',
}

export function useSearchCrossroadsEntities(
  searchTerm: string,
  objectType: string,
  enabled: boolean = true,
  parentGoid?: string,
) {
  const { selectedTenant } = useTenantStore()

  return useQuery({
    queryKey: [
      'crossroads-entity-search',
      selectedTenant?.name,
      searchTerm,
      objectType,
      parentGoid,
    ],
    queryFn: async (): Promise<EntityObject[]> => {
      // Allow empty search term to fetch all entities
      if (searchTerm && searchTerm.length > 0 && searchTerm.length < 2)
        return []

      const endpoint = entityEndpointMap[objectType]
      if (!endpoint) {
        console.error(`Unknown entity type: ${objectType}`)
        return []
      }

      try {
        const { data, error } = await fetchClient.GET(endpoint, {})

        if (error) {
          throw new Error(`API error: ${JSON.stringify(error)}`)
        }

        // Handle response - basic endpoints return arrays directly
        let entities: EntityObject[] = []
        if (Array.isArray(data)) {
          entities = data as unknown as EntityObject[]
        }

        // Filter tanks by parent site GOID if provided
        if (objectType === 'tank' && parentGoid) {
          const filteredEntities = entities.filter((entity: EntityObject) => {
            // Check if the tank belongs to the parent site
            const siteGoid =
              entity['site_goid'] ||
              entity['site'] ||
              entity['parent_goid'] ||
              (entity['data'] as Record<string, unknown> | undefined)?.[
                'site_goid'
              ] ||
              (entity['data'] as Record<string, unknown> | undefined)?.[
                'site'
              ] ||
              (entity['data'] as Record<string, unknown> | undefined)?.[
                'parent_goid'
              ]
            return siteGoid === parentGoid
          })
          return filteredEntities
        }

        return entities
      } catch (error) {
        console.error(
          'useSearchCrossroadsEntities error:',
          error,
          'endpoint:',
          endpoint,
        )
        throw error
      }
    },
    enabled:
      !!objectType &&
      enabled &&
      !!selectedTenant?.name &&
      (searchTerm.length === 0 || searchTerm.length >= 2),
    staleTime: 60000, // Cache for 1 minute
  })
}

// Helper function to get the correct object type for API calls
export const getObjectType = (entityType: string): string => {
  const typeMap: Record<string, string> = {
    company: 'counterparty',
    counterparty: 'counterparty',
    product: 'product',
    terminal: 'terminal',
    site: 'site',
    tank: 'tank',
  }
  return typeMap[entityType] || entityType
}
